import { AbstractCommand } from './AbstractCommand';
import { VideoEditor } from '../VideoEditor';
import { VideoElement } from '../models/elements/VideoElement';
import { Track } from '../models/Track';
import { TrackManager } from '../lib/TrackManager';
import { TimelineMode } from './TimelineMode';
import {
  TranscriptTextElement,
  TranscriptElement,
} from '@src/types.ts/transcript';
import { Transcript } from '../models/Transcript';
import { TranscriptNavigator } from '../models/TranscriptNavigator';
import { v4 as uuid } from 'uuid';
import { Video } from '@src/types.ts/story';
import { AddLayoutElementsCommand } from './AddLayoutElementsCommand';
import { TrimLayoutElementsCommand } from './TrimLayoutElementsCommand';
import MergeLayoutElementsCommand from './MergeLayoutElementsCommand';

type OperationType = 'merge' | 'extendPrev' | 'extendNext' | 'insert' | 'none';

/**
 * TranscriptSimpleRestoreCommand – restores a contiguous range of transcript
 * words (specified by their original `initial_index` values).  It looks at
 * the neighbouring VideoElements (if any) to determine the minimal timeline
 * operation required:
 *   • merge  – when the range lies exactly between two adjacent clips
 *   • extendPrev – when the range sits immediately after the previous clip
 *   • extendNext – when the range sits immediately before the next clip
 *   • insert – when no adjacent clip touches the range (paste anywhere)
 *
 * The command stores a snapshot of the entire transcript track before
 * execution, allowing undo to simply restore that snapshot.
 */
export class TranscriptSimpleRestoreCommand extends AbstractCommand {
  private readonly prevElementId: string | null;
  private readonly nextElementId: string | null;
  private readonly startInitialIdx: number;
  private readonly endInitialIdx: number;

  private transcriptTrack: Track | null = null;
  private readonly trackManager: TrackManager;
  private readonly transcript: Transcript;
  private readonly navigator: TranscriptNavigator;

  // Snapshot for undo (memento pattern)
  private originalTrackState: {
    id: string;
    state: Record<string, any>;
  }[] = [];

  // Inserted element info (for undo)
  private insertCmd: {
    element: VideoElement;
    trackId: string;
    shiftAmount: number;
    extraElementDataSnapshot?: Video['extraElementData'];
  } | null = null;

  constructor(
    editor: VideoEditor,
    prevElementId: string | null,
    nextElementId: string | null,
    startInitialIdx: number,
    endInitialIdx: number,
  ) {
    super(editor);

    this.prevElementId = prevElementId;
    this.nextElementId = nextElementId;
    this.startInitialIdx = Math.min(startInitialIdx, endInitialIdx);
    this.endInitialIdx = Math.max(startInitialIdx, endInitialIdx);
    this.transcript = editor.transcript!;
    this.navigator = new TranscriptNavigator(this.transcript, editor);

    // Log constructor parameters for debugging

    this.trackManager = this.editor.getTrackManager();

    const track = this.getTranscriptTrack();
    if (track) {
      this.originalTrackState = track.elements.map((el) => ({
        id: el.id,
        state: el.getState(),
      }));
    }
  }

  // -------------------------------------------------------------------
  // Command lifecycle
  // -------------------------------------------------------------------

  execute(): boolean {
    const track = this.getTranscriptTrack();
    if (!track) {
      return false;
    }

    const words = this.collectWords();
    if (!words.length) {
      console.error(
        'TranscriptSimpleRestoreCommand: No words to restore in specified range',
      );
      return false;
    }

    const { prev, next } = this.lookupNeighbours();
    const op = this.determineOperation(prev, next);

    switch (op) {
      case 'merge':
        if (!prev || !next) {
          console.error(
            'TranscriptSimpleRestoreCommand: Missing prev or next for merge operation',
          );
          break;
        }
        this.applyMerge(prev, next);
        break;
      case 'extendPrev':
        if (!prev) {
          console.error(
            'TranscriptSimpleRestoreCommand: Missing prev for extendPrev operation',
          );
          break;
        }
        this.extendPrev(prev, words[words.length - 1] as TranscriptTextElement);
        break;
      case 'extendNext':
        if (!next) {
          console.error(
            'TranscriptSimpleRestoreCommand: Missing next for extendNext operation',
          );
          break;
        }
        this.extendNext(next, words[0] as TranscriptTextElement);
        break;
      case 'insert':
        this.performInsert(track, prev, next);
        break;
      case 'none':
        return false;
    }

    // Recalculate timeline and mark dirty
    this.editor.timeline.recalculateDuration();
    this.editor.markDirty('transcript_simple_restore');

    return true;
  }

  undo(): boolean {
    const track = this.getTranscriptTrack();
    if (!track) return false;

    // If we performed an insert, undo it first
    if (this.insertCmd) {
      // Use trackManager to remove the inserted element with ripple
      this.trackManager.undoInsertElement(
        this.insertCmd.element,
        this.insertCmd.shiftAmount,
        track,
      );
      if (this.insertCmd?.extraElementDataSnapshot) {
        this.extraElementData.restoreValue(
          this.insertCmd.extraElementDataSnapshot,
        );
      }
      this.insertCmd = null;

      this.editor.timeline.recalculateDuration();
      this.editor.markDirty('transcript_simple_restore_undo_insert');
      return true;
    }

    // Otherwise we restore the entire track snapshot we saved originally.
    // Remove current elements
    [...track.elements].forEach((el) => {
      this.trackManager.removeElement(el, TimelineMode.DEFAULT);
    });

    // Restore saved state
    for (const saved of this.originalTrackState) {
      const restored = VideoElement.fromJSON(saved.state);
      // Reattach transcript
      if ((saved.state as any).transcriptSegment) {
        restored.setOriginalTranscription(
          Array.from(this.editor.transcript?.getOriginalTranscription() || []),
        );
      }
      this.trackManager.addElement(restored, track, TimelineMode.DEFAULT);
    }

    this.editor.timeline.recalculateDuration();
    this.editor.markDirty('transcript_simple_restore_undo');

    return true;
  }

  getDescription(): string {
    return `Simple restore words (${this.startInitialIdx}-${this.endInitialIdx})`;
  }

  // -------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------

  private getTranscriptTrack(): Track | null {
    if (this.transcriptTrack) return this.transcriptTrack;
    const track = this.editor.getTranscriptTrack();
    if (!track) return null;
    this.transcriptTrack = track;
    return track;
  }

  private lookupNeighbours(): {
    prev: VideoElement | null;
    next: VideoElement | null;
  } {
    let prev: VideoElement | null = null;
    let next: VideoElement | null = null;

    if (this.prevElementId) {
      const el = this.editor.getElementById(this.prevElementId);
      if (el && el.type === 'video') prev = el as VideoElement;
    }

    if (this.nextElementId) {
      const el = this.editor.getElementById(this.nextElementId);
      if (el && el.type === 'video') next = el as VideoElement;
    }

    return { prev, next };
  }

  private determineOperation(
    prev: VideoElement | null,
    next: VideoElement | null,
  ): OperationType {
    const prevContiguous = prev && this.prevTouchesRange(prev) ? true : false;
    const nextContiguous = next && this.nextTouchesRange(next) ? true : false;

    if (prevContiguous && nextContiguous) return 'merge';
    if (prevContiguous) return 'extendPrev';
    if (nextContiguous) return 'extendNext';
    if (!prev && !next) return 'insert';
    // Could insert between prev and next if gap, treat as insert
    return 'insert';
  }

  /**
   * Checks if the previous element and the current segment are continuous
   * by analyzing the original transcript using the TranscriptNavigator
   */
  private prevTouchesRange(prev: VideoElement): boolean {
    const seg = prev.getTranscriptSegment();
    if (!seg) {
      return false;
    }

    // Get all elements from the segment, including punctuation
    const segmentElements = seg.getElementsWithPunctuation();
    if (!segmentElements.length) {
      return false;
    }

    // Use the navigator to check if the previous segment and our range are continuous
    const isContiguous = this.navigator.isPrevSegmentContinuousWithRange(
      segmentElements,
      this.startInitialIdx,
    );

    return isContiguous;
  }

  /**
   * Checks if the next element and the current segment are continuous
   * by analyzing the original transcript using the TranscriptNavigator
   */
  private nextTouchesRange(next: VideoElement): boolean {
    const seg = next.getTranscriptSegment();
    if (!seg) {
      return false;
    }

    // Get all elements from the segment, including punctuation
    const segmentElements = seg.getElementsWithPunctuation();
    if (!segmentElements.length) {
      return false;
    }

    // Use the navigator to check if our range and the next segment are continuous
    const isContiguous = this.navigator.isRangeContinuousWithNextSegment(
      this.endInitialIdx,
      segmentElements,
    );

    return isContiguous;
  }

  private applyMerge(prev: VideoElement, next: VideoElement): void {
    // Merge and rely on TrackManager's ripple handling
    const track = this.getTranscriptTrack();
    if (!track) return;

    if (
      this.extraElementData.isLayoutElement(prev) ||
      this.extraElementData.isLayoutElement(next)
    ) {
      const command = new MergeLayoutElementsCommand(this.editor, undefined, {
        prev,
        next,
        track,
      });
      command.execute();
    } else {
      // store original states in snapshot already, so just merge
      this.trackManager.bridgeAndMergeElements([prev, next], track);
    }
  }

  private extendPrev(
    prev: VideoElement,
    lastWord: TranscriptTextElement,
  ): void {
    // Calculate new duration based on the difference between the element's trim_start and the last word's end_ts
    // This gives us the duration from the clip start to the end of the last word in original media time
    const newDuration = lastWord.end_ts - (prev.trim_start ?? 0);

    // Calculate the corresponding end time on the timeline
    const newEndTime = prev.time + newDuration;

    if (this.extraElementData.isLayoutElement(prev)) {
      // Trim but for all 3 elements that make up stacked layout
      const command = new TrimLayoutElementsCommand(this.editor, prev, {
        trimType: 'end',
        trimTime: newEndTime,
      });
      command.execute();
    } else {
      // Use track manager to extend the element's end time on the timeline
      this.trackManager.trimElementEnd(
        prev,
        newEndTime,
        TimelineMode.RIPPLE,
        false,
      );
    }
  }

  private extendNext(
    next: VideoElement,
    firstWord: TranscriptTextElement,
  ): void {
    // Get the transcript segment for the next element
    const nextSegment = next.getTranscriptSegment();
    if (!nextSegment) {
      console.error('Cannot extend next without a transcript segment');
      return;
    }

    if (this.extraElementData.isLayoutElement(next)) {
      // Trim but for all 3 elements that make up stacked layout
      const command = new TrimLayoutElementsCommand(this.editor, next, {
        trimType: 'start',
        trimTime: firstWord.ts,
      });
      command.execute();
    } else {
      // Set the new trim_start to start at the first word's timestamp in the original media
      // This will extend the clip to include all words between firstWord and the current clip start
      this.trackManager.trimMediaStart(
        next,
        firstWord.ts,
        TimelineMode.RIPPLE,
        false,
      );
    }
  }

  private performInsert(
    track: Track,
    prev: VideoElement | null,
    next: VideoElement | null,
  ): void {
    const words = this.collectWords();
    if (!words.length) {
      console.error('Insert: No words to insert!');
      return;
    }

    // Determine insertion time
    let time = 0;

    if (prev) {
      time = prev.end_time;
    } else if (next) {
      // Insert before next
      const duration = this.estimateDuration();
      time = Math.max(0, next.time - duration);
    }

    // Calculate the duration based on the words we're restoring
    const duration = this.estimateDuration();
    if (duration <= 0) {
      console.error('Insert: Invalid duration for new element:', duration);
      return;
    }

    // Create a new video element
    // If track has elements, base the new element on an existing one
    const sourceElement = track.elements.find((el) => el.type === 'video') as
      | VideoElement
      | undefined;

    // Create a new element based on a source element or create a fresh one
    const newElement = sourceElement
      ? sourceElement.clone()
      : new VideoElement();

    // Set the element properties
    newElement.update({
      id: `inserted-${uuid()}`,
      time: time,
      trim_start: words[0].ts,
      duration: duration,
    });

    // If the element is based on a source element, make sure it has the transcript
    if (this.editor.transcript) {
      newElement.setOriginalTranscription(
        Array.from(this.editor.transcript.getOriginalTranscription() || []),
      );
    }

    // Add the element to the track

    // Store information about the inserted element for undo
    this.insertCmd = {
      element: newElement,
      trackId: track.id,
      shiftAmount: duration,
    } as any; // Type compatibility with the previous insert command

    // Use trackManager to add the element with ripple
    this.trackManager.addElement(newElement, track, TimelineMode.RIPPLE);

    if (sourceElement && this.extraElementData.isLayoutElement(sourceElement)) {
      const { layoutType } =
        this.extraElementData.findLayoutRelatedElements(sourceElement);
      this.insertCmd!.extraElementDataSnapshot =
        this.extraElementData.cloneValue();
      if (layoutType) {
        const command = new AddLayoutElementsCommand(this.editor, newElement, {
          layoutType: layoutType as 'stacked-1-1' | 'stacked-1-3',
        });
        command.execute();
      }
    }
  }

  private collectWords(): TranscriptTextElement[] {
    if (!this.transcript) return [];

    // Use the navigator to collect text elements in the specified range
    return this.navigator.getTextElementsInRange(
      this.startInitialIdx,
      this.endInitialIdx,
    );
  }

  private estimateDuration(): number {
    const words = this.collectWords();
    if (!words.length) return 0;
    return words[words.length - 1].end_ts - words[0].ts;
  }
}
